import { useTranslation } from "react-i18next";
import Dialog, { useDialogContext } from "@imminently/decisively-ui/Dialog";
import { DialogHeader } from "@/ui/dialogs/DialogHeader";
import Form from "@imminently/decisively-ui/Form";
import { workspaceService } from "@/app/providers/services";
import { useNotify } from "@/common/notifications";
import type { Workspace } from "@packages/commons";
import { WorkspaceForm } from "./WorkspaceForm";
import { workspaceSchema, type WorkspaceFormValues } from "../types";

interface WorkspaceModalProps {
  workspace?: Workspace;
  close: () => void;
}

export const WorkspaceModal = ({ workspace, close }: WorkspaceModalProps) => {
  const { t } = useTranslation();
  const dialogContext = useDialogContext();
  const notify = useNotify();
  const isEditing = !!workspace;

  const { action, query } = workspaceService.useMutateResource(
    workspace as Workspace,
  );
  const { isLoading } = query;
  const handleSubmit = async (values: WorkspaceFormValues) => {
    try {
      await action({
        name: values.name,
        visibility: values.visibility,
        ...(values.aiKey ? { aiKey: values.aiKey } : {}),
        users: values.users.map((u: any) => u.identity_id ?? u.id),
      });
      workspaceService.invalidateAllQueries();
      dialogContext?.close();
    } catch {
      notify.error(
        isEditing
          ? t("workspaces.update_error")
          : t("workspaces.create_error"),
      );
    }
  };

  const title = isEditing ? t("workspaces.edit") : t("workspaces.create");

  return (
    <Dialog.Content className="flex flex-col">
      <DialogHeader
        title={title}
        description={!isEditing ? t("workspaces.create_description") : undefined}
      />

      <Form
        className="overflow-y-auto"
        schema={workspaceSchema as any}
        defaultValues={{
          name: workspace?.name ?? "",
          visibility: (workspace?.visibility as "public" | "private") ?? "public",
          users: (workspace as any)?.usersData ?? [],
          aiKey: "",
        }}
        onSubmit={handleSubmit}
      >
        <Dialog.Body>
          <WorkspaceForm isEditing={isEditing} />
        </Dialog.Body>
        <Dialog.Actions>
          <Dialog.Actions.PrimaryButton
            label={isEditing ? t("save") : t("workspaces.create")}
            type="submit"
            loading={isLoading}
            disabled={isLoading}
          />
        </Dialog.Actions>
      </Form>
    </Dialog.Content>
  );
};
